import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Formik, Form, FieldArray, useFormikContext } from 'formik';
import * as yup from 'yup';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { Spinner } from '@crazyegginc/hatch';

import { DashboardPage } from '/src/components/Page';
import { SEO } from '/src/components/SEO';
import { DashHeader } from '/src/components/headers/DashHeader';
import { funnelDetailQuery } from '../queries';
import { SiteSelectorModal } from '../components/modals/SiteSelectorModal';
import { useSite, useSelectedSite, useMutation, useNotifications, usePermissions, useAuthContext } from '/src/hooks';
import { FEATURES, ALL } from '/src/features/_global/constants';
import { getFeatureName } from '/src/features/_global/utils';
import { goalRefListQuery } from '/src/features/goals/queries';
import { FunnelEditorProvider, useFunnelEditorContext } from '../funnel-editor-context';
import { StepButton } from '../components/report/StepButton';

import { ReportHeader } from '../components/report/ReportHeader';
import { FunnelSteps } from '../components/report/FunnelSteps';
import { AddStepWall } from '../components/paywalls/AddStepWall';
import { CreateGoalWall } from '../components/paywalls/CreateGoalsWall';
import { FunnelResults } from '../components/report/FunnelResults';
import { STEP_TYPES, MAX_STEPS, ENTER_SITE } from '../constants';
import { convertStepsToEditorFormat, convertGoalStepsToDBFormat } from '../utils';
import { createFunnelMutation, editFunnelMutation } from '../mutations';

import { ReactComponent as FunnelIcon } from '@crazyegginc/hatch/dist/images/icon-filter-filled.svg';
import { ReactComponent as InfoIcon } from '@crazyegginc/hatch/dist/images/icon-info-circle-outline.svg';

const err = 'Please select a Conversion.';
const validationSchema = yup.object().shape({
  name: yup.string().trim().required('Please provide a name.'),
  steps: yup
    .array()
    .of(
      yup.object().shape({
        type: yup.string().oneOf([...Object.values(STEP_TYPES)]),
        id: yup.number().when('type', {
          is: STEP_TYPES.VISIT,
          then: yup.number().nullable(true),
          otherwise: yup.number(err).required(err).typeError(err).integer(err).positive(err),
        }),
      }),
    )
    .max(MAX_STEPS, 'Maximum of ${max} steps reached.'),
});

// eslint-disable-next-line no-restricted-syntax
export default function FunnelEditor() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isInitialLoading, error } = useQuery({
    ...funnelDetailQuery({ id: parseInt(id) }),
    enabled: Boolean(id),
  });

  const { sites, loadingSites, selectSite, selectedSite } = useSite({
    dontSelectSite: true,
  });
  const [origSelectedSite] = useState(selectedSite);

  const { data: goalsData, isFetching: fetchingGoals } = useQuery({
    ...goalRefListQuery({ siteId: selectedSite?.id }),
    enabled: Boolean(selectedSite?.id),
  });

  const goals = goalsData?.goalRefList ?? [];

  useEffect(() => {
    if (error) {
      if (error.message?.match(/Funnel not found/)) {
        navigate('/funnels');
      } else {
        throw error;
      }
    }
  }, [error, navigate]);

  // deselect site if all sites is selected
  useEffect(() => {
    if (selectedSite === ALL) {
      selectSite(null);
    }
  }, [selectSite, selectedSite]);

  // select site if not selected:
  // - if there's only one
  // - or from the funnels data
  useEffect(() => {
    if (!selectedSite?.id) {
      if (!loadingSites && id == null && sites.length === 1) {
        selectSite(sites[0]);
      } else if (!loadingSites && data?.funnelDetail.siteId) {
        selectSite(sites.find((s) => s.id === data?.funnelDetail.siteId));
      }
    }
  }, [id, selectSite, sites, loadingSites, data?.funnelDetail.siteId, selectedSite]);

  // refresh site from query if changed
  useEffect(() => {
    if (!selectedSite?.id || !sites.length) return;

    const selectedSiteFromQuery = sites.find((site) => site.id === selectedSite.id);
    if (selectedSiteFromQuery && selectedSiteFromQuery !== selectedSite) {
      selectSite(selectedSiteFromQuery);
    }
  }, [selectedSite, selectSite, sites]);

  // otherwise show site selector modal
  if (!loadingSites && id == null && sites.length > 1 && !selectedSite?.id) {
    return (
      <SiteSelectorModal
        onCancel={() => navigate('/funnels')}
        initialSite={origSelectedSite?.id ? origSelectedSite : null}
      />
    );
  }

  if ((id && isInitialLoading) || loadingSites || fetchingGoals || !selectedSite?.id) {
    return (
      <DashboardPage>
        <SEO title={getFeatureName(FEATURES.FUNNELS)} />
        <DashHeader title={getFeatureName(FEATURES.FUNNELS)} />
        <div className="mt-16 flex w-full items-center justify-center">
          <Spinner />
          <div className="ml-2.5">Loading...</div>
        </div>
      </DashboardPage>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <FunnelEditorProvider funnel={data?.funnelDetail}>
        <EditorContent goals={goals} />
      </FunnelEditorProvider>
    </DndProvider>
  );
}

function EditorContent({ goals }) {
  const { funnel, setFunnel } = useFunnelEditorContext();
  const { selectedSite } = useSelectedSite();
  const permissions = usePermissions();
  const canEditFunnel = permissions.can('edit', FEATURES.FUNNELS).allowed;

  const [initData] = useState(
    funnel
      ? {
          name: funnel.name,
          steps: convertStepsToEditorFormat(funnel.visitStep, funnel.goalSteps),
        }
      : { name: '', steps: [] },
  );

  const { mutate: createFunnel } = useMutation(createFunnelMutation);
  const { mutate: editFunnel } = useMutation(editFunnelMutation);
  const notifications = useNotifications();
  const navigate = useNavigate();

  function saveFunnel(values, { setSubmitting, resetForm }) {
    if (!canEditFunnel) return;
    const params = {
      siteId: funnel?.siteId ?? selectedSite.id,
      name: values.name,
      visitStep: values.steps[0]?.type === STEP_TYPES.VISIT,
      goalSteps: convertGoalStepsToDBFormat(values.steps),
    };
    let mutation = createFunnel;
    if (funnel?.id != null) {
      params.id = funnel.id;
      mutation = editFunnel;
    }

    mutation(params, {
      onError: () => {
        notifications.error({
          title: 'Failed to save Funnel',
          content: '',
          timeout: 4000,
        });
      },
      onSuccess: (data) => {
        const funnel = data.funnelEdit ?? data.funnelCreate;
        if (mutation === createFunnel && !values.inSaveModal) {
          navigate(`/funnels/edit/${funnel.id}`, {
            replace: true,
            state: { submitCount: 1 },
          });
        } else {
          setFunnel(funnel);
          resetForm({ values, submitCount: 1 });
        }
      },
      onSettled: () => {
        setSubmitting(false);
      },
    });
  }

  return (
    <DashboardPage mainClass="!pb-0 flex flex-col">
      <SEO title={getFeatureName(FEATURES.FUNNELS)} />
      <Formik initialValues={initData} validationSchema={validationSchema} onSubmit={saveFunnel}>
        <Form className="contents">
          <ReportHeader />
          <Content goals={goals} />
        </Form>
      </Formik>
    </DashboardPage>
  );
}

function Content({ goals }) {
  const { values } = useFormikContext();
  const { selectStep, selectedStep } = useFunnelEditorContext();

  // when deleting steps, make sure the selected is within the possibilites
  useEffect(() => {
    if (selectedStep >= values.steps.length) {
      selectStep(Math.max(values.steps.length - 1, 0));
    }
  }, [selectedStep, selectStep, values.steps.length]);

  const stepsData = useMemo(
    () =>
      values.steps.map((step, i) => ({
        ...step,
        ...(step.type === STEP_TYPES.GOAL ? { createdAt: goals.find((goal) => goal.id === step.id)?.createdAt } : {}),
        name: i === 0 && step.type === STEP_TYPES.VISIT ? ENTER_SITE : goals.find((goal) => goal.id === step.id)?.name,
      })),
    [goals, values.steps],
  );

  return (
    <div className="flex flex-1">
      <LeftContent goals={goals} stepsData={stepsData} />
      <RightContent goals={goals} stepsData={stepsData} />
    </div>
  );
}

function LeftContent({ goals, stepsData }) {
  const navigate = useNavigate();
  const { selectedSite } = useSelectedSite();
  const { isSharing } = useAuthContext();

  return (
    <section className="w-[350px] shrink-0 border-r border-mystic-500 py-10 px-5">
      <div className="flex items-center space-x-2">
        <FunnelIcon className="fill-current text-cadet-blue-500 w-4 h-4" />
        <span className="text-header-3">Steps</span>
      </div>
      <div className="mt-2.5 border border-mystic-500 p-2.5 rounded-lg">
        {!goals.length && !isSharing ? (
          <>
            <StepButton
              text="Track a Conversion"
              onClick={() => navigate({ pathname: '/goals/new', search: `site=${selectedSite.name}` })}
              blueShadow={true}
            />
          </>
        ) : (
          <FieldArray name={'steps'}>
            {(actions) => <FunnelSteps goals={goals} actions={actions} stepsData={stepsData} />}
          </FieldArray>
        )}
      </div>
      {!goals.length ? (
        <div className="flex items-center text-body-4 mt-3.75 leading-none">
          <InfoIcon className="fill-current text-cadet-blue-500 w-3.5 h-3.5 mr-1.5" />
          <span>Track Conversions for each step in your funnel.</span>
        </div>
      ) : null}
    </section>
  );
}

function RightContent({ goals, stepsData }) {
  const { values } = useFormikContext();
  const { isSharing } = useAuthContext();

  if (!goals.length && !isSharing) {
    return <CreateGoalWall />;
  }
  if (!values.steps.length && !isSharing) {
    return <AddStepWall />;
  }
  return <FunnelResults stepsData={stepsData} goals={goals} />;
}
